/**
 * Receipts at the register (T07-25): the bytes are pos-service's (T07-09 — 適格簡易請求書 drawn
 * with the Noto fonts, QR, cut); the tablet asks for them and hands them to the print queue.
 *
 * - `GET /pos/orders/{id}/receipt?format=escpos&width=&drawer=false&cut=true` — the first fetch is
 *   the original, every later one is printed 「再発行」 and counted (reprint_count) at the server;
 *   the drawer kick is sent separately (a cash sale opens the drawer even if printing fails);
 * - a retry of a failed print re-sends the same bytes (still the original);
 * - the last 200 receipts are kept in Dexie for a reprint while offline.
 */
import type { PosClient } from "@tcg/api-client";

import { call } from "./api";
import { cacheReceipt, type PosDatabase, type PrintJob } from "./db";
import type { PrinterManager } from "./printer";
import type { Receipt } from "./types";

export async function fetchReceipt(
  api: PosClient,
  saleId: string,
  query: { format: "escpos" | "html" | "json" | "pdf"; width?: number; locale?: string },
): Promise<Receipt> {
  return call(() =>
    api.GET("/v1/pos/orders/{sale_id}/receipt", {
      params: {
        path: { sale_id: saleId },
        query: {
          format: query.format,
          ...(query.width ? { width: query.width } : {}),
          ...(query.format === "escpos" ? { drawer: false, cut: true } : {}),
          ...(query.locale ? { locale: query.locale } : {}),
        },
      },
    }),
  );
}

export interface PrintOutcome {
  job: PrintJob | null;
  receipt: Receipt | null;
  error: unknown;
}

/** Fetch the ESC/POS receipt and print it through the queue (never throws). */
export async function printReceipt(options: {
  api: PosClient;
  db: PosDatabase;
  printer: PrinterManager;
  saleId: string;
  label: string;
  /** Language of the second lines (免税: the buyer's — T07-30). */
  locale?: string;
}): Promise<PrintOutcome> {
  const { api, db, printer, saleId, label } = options;
  let receipt: Receipt;
  try {
    receipt = await fetchReceipt(api, saleId, {
      format: "escpos",
      width: printer.config.paperWidth,
      ...(options.locale ? { locale: options.locale } : {}),
    });
  } catch (error) {
    return { job: null, receipt: null, error };
  }
  if (!receipt.data) {
    return { job: null, receipt, error: new Error("empty receipt") };
  }
  await cacheReceipt(db, {
    receiptNo: receipt.receiptNo,
    saleId,
    data: receipt.data,
    paperWidth: receipt.paperWidth ?? printer.config.paperWidth,
    createdAt: Date.now(),
  });
  const job = await printer.queue.submit({
    label: receipt.receiptNo || label,
    saleId,
    receiptNo: receipt.receiptNo,
    data: receipt.data,
  });
  return { job, receipt, error: null };
}

/**
 * The QR is the digital receipt link (`<site>/r/<receiptNo>?t=…`, T10-34 — pos-service prints it
 * when `POS_RECEIPT_QR_BASE_URL` + `POS_RECEIPT_LINK_SECRET` are set, compose's default) rather
 * than the bare receipt number → the screen and the paper say 「電子レシート」.
 */
export function isReceiptLink(value: string | null | undefined): boolean {
  return typeof value === "string" && /^https?:\/\/[^\s]+\/r\/[^\s?]+\?t=/.test(value);
}

/** What the receipt's QR carries (the digital receipt link, else the receipt number). */
export function qrContentOf(receipt: Receipt | null | undefined): string | null {
  const payload = receipt?.payload;
  const find = (value: unknown): string | null => {
    if (!value || typeof value !== "object") {
      return null;
    }
    const record = value as Record<string, unknown>;
    if (typeof record.qrContent === "string" && record.qrContent) {
      return record.qrContent;
    }
    for (const child of Object.values(record)) {
      const hit = find(child);
      if (hit) {
        return hit;
      }
    }
    return null;
  };
  return find(payload) ?? receipt?.receiptNo ?? null;
}

/** The 返品 / 取消 slip of a return or void (T07-27) → the print queue (never throws). */
export async function printReturnSlip(options: {
  api: PosClient;
  printer: PrinterManager;
  returnId: string;
  label: string;
}): Promise<PrintOutcome> {
  const { api, printer, returnId, label } = options;
  let receipt: Receipt;
  try {
    receipt = await call(() =>
      api.GET("/v1/pos/returns/{return_id}/receipt", {
        params: {
          path: { return_id: returnId },
          query: { format: "escpos", width: printer.config.paperWidth, drawer: false, cut: true },
        },
      }),
    );
  } catch (error) {
    return { job: null, receipt: null, error };
  }
  if (!receipt.data) {
    return { job: null, receipt, error: new Error("empty slip") };
  }
  const job = await printer.queue.submit({
    label: receipt.receiptNo || label,
    receiptNo: receipt.receiptNo,
    data: receipt.data,
  });
  return { job, receipt, error: null };
}
